import type {
	IChartingLibraryWidget,
	LanguageCode,
	Timezone,
} from "@public/charting_library";
import { useCallback, useEffect, useRef, useState } from "react";
import type { SupportedLocale } from "#/lib/locale.ts";
import { useTradePerpMarkets } from "../../../hooks/use-trade-data.ts";
import { resolveChartTimezone } from "../../../lib/chart-locale.ts";
import { createHyperliquidDatafeed } from "../../../lib/hyperliquid-datafeed.ts";
import { loadTradingViewLibrary } from "../../../lib/load-trading-view.ts";
import {
	CHART_FAVORITE_INTERVALS,
	CHART_FONT_FAMILY,
	CHART_TIME_FRAMES,
	chartThemeConfig,
	DEFAULT_INTERVAL,
	getChartOverrides,
} from "../constants/chart-theme.ts";
import {
	createChartSettingsAdapter,
	loadChartPreferences,
	saveChartPreference,
} from "../lib/chart-storage.ts";
import {
	applyChartTheme,
	attachChartEventHandlers,
	type ChartEventHandlers,
	saveChartState,
} from "../lib/chart-widget.ts";
import {
	hasSavedChartsInAdapter,
	LocalStorageSaveLoadAdapter,
} from "../lib/local-storage-save-load-adapter.ts";

type UseTradingViewWidgetParams = {
	hasHydrated: boolean;
	locale: SupportedLocale;
	isMobile: boolean;
	onChartSymbolChanged?: (symbol: string) => void;
	requestedSymbol: string;
	resolvedTheme: keyof typeof chartThemeConfig;
};

export function useTradingViewWidget({
	hasHydrated,
	isMobile,
	locale,
	onChartSymbolChanged,
	requestedSymbol,
	resolvedTheme,
}: UseTradingViewWidgetParams) {
	const { markets } = useTradePerpMarkets();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const widgetRef = useRef<IChartingLibraryWidget | null>(null);
	const datafeedRef = useRef<ReturnType<
		typeof createHyperliquidDatafeed
	> | null>(null);
	const chartReadyRef = useRef(false);
	const currentSymbolRef = useRef(requestedSymbol);
	const requestedSymbolRef = useRef(requestedSymbol);
	const searchableMarketsRef = useRef(markets);
	const onChartSymbolChangedRef = useRef(onChartSymbolChanged);
	const resolvedThemeRef = useRef(resolvedTheme);
	// Tracks the theme the live widget was last synced to, so theme toggles
	// after widget creation can apply via `changeTheme` instead of triggering
	// a full re-init of the ~2.5MB charting library.
	const widgetSyncedThemeRef = useRef<keyof typeof chartThemeConfig | null>(
		null,
	);
	const [isWidgetReady, setIsWidgetReady] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);

	requestedSymbolRef.current = requestedSymbol;
	searchableMarketsRef.current = markets;
	onChartSymbolChangedRef.current = onChartSymbolChanged;
	resolvedThemeRef.current = resolvedTheme;

	const handleLoadError = useCallback((message: string) => {
		setLoadError(message);
	}, []);

	const handleLoadSuccess = useCallback(() => {
		setLoadError(null);
	}, []);

	const retryLoad = useCallback(() => {
		setRefreshKey((k) => k + 1);
	}, []);

	useEffect(() => {
		void refreshKey;
		if (
			!hasHydrated ||
			!containerRef.current ||
			typeof window === "undefined"
		) {
			return;
		}

		let cancelled = false;
		let widget: IChartingLibraryWidget | null = null;
		let datafeed: ReturnType<typeof createHyperliquidDatafeed> | null = null;
		let teardownEvents: (() => void) | null = null;

		chartReadyRef.current = false;
		setIsWidgetReady(false);
		setLoadError(null);

		void loadTradingViewLibrary()
			.catch((error) => {
				console.warn("[Chart] TradingView library failed to load", error);
				return undefined;
			})
			.then(() => {
				if (cancelled || !window.TradingView || !containerRef.current) {
					return;
				}

				datafeed = createHyperliquidDatafeed({
					getSearchableMarkets: () => searchableMarketsRef.current,
					onLoadError: handleLoadError,
					onLoadSuccess: handleLoadSuccess,
				});
				datafeedRef.current = datafeed;

				const initialTheme = resolvedThemeRef.current;
				const themeConfig = chartThemeConfig[initialTheme];
				const overrides = getChartOverrides(initialTheme, isMobile);
				const initialSymbol = requestedSymbolRef.current;
				const hasSavedCharts = hasSavedChartsInAdapter();
				const storedPreferences = loadChartPreferences();
				const settingsAdapter = createChartSettingsAdapter();
				currentSymbolRef.current = initialSymbol;

				widget = new window.TradingView.widget({
					autosize: true,
					auto_save_delay: 1,
					container: containerRef.current,
					custom_font_family: CHART_FONT_FAMILY,
					datafeed: datafeed as TradingView.IBasicDataFeed,
					disabled_features: [
						"use_localstorage_for_settings",
						"save_chart_properties_to_local_storage",
						"header_compare",
						"header_fullscreen_button",
						"header_screenshot",
						"header_symbol_search",
					],
					enabled_features: [
						"left_toolbar",
						"iframe_loading_same_origin",
						"prefer_symbol_name_over_fullname",
						"items_favoriting",
						"header_indicators",
						isMobile ? "hide_left_toolbar_by_default" : "symbol_info",
					],
					favorites: {
						intervals: CHART_FAVORITE_INTERVALS,
					},
					interval: storedPreferences?.interval ?? DEFAULT_INTERVAL,
					library_path: "/charting_library/",
					locale: locale as LanguageCode,
					timezone: resolveChartTimezone(locale) as Timezone,
					loading_screen: themeConfig.loadingScreen,
					load_last_chart: true,
					custom_css_url: "/themed.css",
					overrides,
					save_load_adapter: LocalStorageSaveLoadAdapter,
					settings_adapter: settingsAdapter,
					symbol: hasSavedCharts ? undefined : initialSymbol,
					theme: initialTheme,
					time_frames: [...CHART_TIME_FRAMES],
				}) as unknown as IChartingLibraryWidget;
				widgetSyncedThemeRef.current = initialTheme;
				widgetRef.current = widget;

				const boundWidget = widget;
				const handlers: ChartEventHandlers = {
					onIntervalChanged: (interval) => {
						saveChartPreference("interval", interval);
					},
					onChartTypeChanged: (chartType) => {
						saveChartPreference("chartType", chartType);
					},
					onSymbolChanged: (symbolInfo) => {
						if (cancelled || widgetRef.current !== boundWidget) return;
						const nextSymbol = symbolInfo.ticker ?? symbolInfo.name;
						if (!nextSymbol || nextSymbol === currentSymbolRef.current) return;
						currentSymbolRef.current = nextSymbol;
						onChartSymbolChangedRef.current?.(nextSymbol);
					},
					onAutoSaveNeeded: () => {
						if (cancelled || widgetRef.current !== boundWidget) return;
						void saveChartState(boundWidget);
					},
				};

				boundWidget.onChartReady(() => {
					if (cancelled || widgetRef.current !== boundWidget) return;

					teardownEvents = attachChartEventHandlers(boundWidget, handlers);
					applyChartTheme(boundWidget, overrides);

					const activeChart = boundWidget.activeChart();
					if (typeof storedPreferences?.chartType === "number") {
						activeChart.setChartType(storedPreferences.chartType);
					}
					if (hasSavedCharts) {
						void activeChart.setSymbol(initialSymbol);
					}

					chartReadyRef.current = true;
					setIsWidgetReady(true);
				});
			});

		return () => {
			cancelled = true;
			const wasReady = chartReadyRef.current;
			widgetRef.current = null;
			chartReadyRef.current = false;
			setIsWidgetReady(false);

			if (!widget) {
				datafeedRef.current = null;
				datafeed?.destroy();
				return;
			}

			// Only call methods that queue work via `_doWhenInnerApiLoaded` when
			// the inner window is loaded. Otherwise the queued callback fires
			// after `widget.remove()` against a null contentWindow and throws
			// "Cannot read properties of null (reading 'doWhenApiIsReady')".
			if (wasReady) {
				void saveChartState(widget);
				teardownEvents?.();
			}

			datafeedRef.current = null;
			datafeed?.destroy();
			try {
				widget.remove();
			} catch {
				// `remove()` can throw if the widget is mid-init; ignore.
			}
		};
	}, [
		hasHydrated,
		isMobile,
		locale,
		handleLoadError,
		handleLoadSuccess,
		refreshKey,
	]);

	// Apply theme changes without re-creating the widget. On initial mount the
	// widget is already created with the right theme (see widgetSyncedThemeRef
	// set in the main effect), so we just re-apply overrides synchronously.
	// On a real toggle, overrides must wait until `changeTheme` resolves —
	// otherwise TV's default theme settings overwrite our pane background
	// when the promise lands.
	useEffect(() => {
		const widget = widgetRef.current;
		if (!widget || !isWidgetReady) return;

		const overrides = getChartOverrides(resolvedTheme, isMobile);

		if (widgetSyncedThemeRef.current === resolvedTheme) {
			applyChartTheme(widget, overrides);
			return;
		}

		widgetSyncedThemeRef.current = resolvedTheme;
		let cancelled = false;
		void widget
			.changeTheme(resolvedTheme)
			.then(() => {
				if (cancelled || widgetRef.current !== widget) return;
				applyChartTheme(widget, overrides);
			})
			.catch((error) => {
				console.warn("[Chart] Failed to change theme.", error);
				if (cancelled || widgetRef.current !== widget) return;
				applyChartTheme(widget, overrides);
			});

		return () => {
			cancelled = true;
		};
	}, [resolvedTheme, isMobile, isWidgetReady]);

	return {
		containerRef,
		widgetRef,
		chartReadyRef,
		currentSymbolRef,
		isWidgetReady,
		loadError,
		retryLoad,
		handleLoadError,
		handleLoadSuccess,
	};
}
